// Explosion aftermath (owner 2026-11-01: 'explosions need a bit more smoke, visual effects and dust, and rocks flying
// everywhere'). Render-only and pooled: billowing alpha-blended smoke puffs (cel two-tone, they grow, rise and drift), a low
// dust skirt rolling outward along the ground, and solid rock chunks thrown ballistically that bounce, tumble and settle.
// Caps keep late-game crowds cheap; the crowd governor thins new emissions when pools are busy.
import * as THREE from 'three/webgpu';
import { abs, attribute, color, cos, dot, float, instancedBufferAttribute, mix, mx_noise_float, normalView, positionGeometry, positionViewDirection, sin, smoothstep, texture, time, uniform, uv, vec3 } from 'three/tsl';
import { mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { CROWD } from './fx';
import { RenderStyle, toonMaterial } from './toon';
import { DEBRIS } from './debrisLook';

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _e = new THREE.Euler();
/** a11y (Reduce flashing): 1 as authored, lower = calmer white-hot cores and Pulse Nova arcs; view.ts sets it from FLASH.dim */
export const blastFlashK = uniform(1);

/** cel smoke puff: a lumpy disc with a hard shaded/lit split and a soft outer edge */
function puffTexture(): THREE.Texture {
  const S = 128, c = document.createElement('canvas'); c.width = c.height = S; const g = c.getContext('2d')!;
  const blob = (x: number, y: number, r: number, fill: string) => { g.fillStyle = fill; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill(); };
  // R = coverage, G = light (1 on the sunny upper-left, 0 in the shade)
  g.fillStyle = 'rgb(0,0,0)'; g.fillRect(0, 0, S, S);
  const lobes: [number, number, number][] = [[64, 70, 36], [42, 60, 24], [86, 58, 26], [56, 40, 22], [80, 82, 22], [44, 86, 20]];
  g.filter = 'blur(2px)';
  for (const [x, y, r] of lobes) blob(x, y, r, 'rgb(255,0,0)');
  g.globalCompositeOperation = 'lighter';
  for (const [x, y, r] of lobes) blob(x - r * 0.22, y - r * 0.25, r * 0.72, 'rgb(0,255,0)');
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.NoColorSpace; return t;
}

class Smoke {
  readonly mesh: THREE.InstancedMesh;
  private n = 0; private d: Float32Array; private alpha: THREE.InstancedBufferAttribute;
  private static S = 13; // x y z vx vy vz life max size0 size1 r g b
  constructor(private cap: number) {
    this.d = new Float32Array(cap * Smoke.S);
    this.alpha = new THREE.InstancedBufferAttribute(new Float32Array(cap), 1);
    const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: true });
    const tx = texture(puffTexture(), uv());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const a = instancedBufferAttribute(this.alpha) as any;
    const lit = smoothstep(float(0.35), float(0.55), tx.g);
    // instanceColor is the shade tone; the lit side is a lighter version of it
    mat.colorNode = vec3(0.62, 0.6, 0.62).add(vec3(0.63, 0.62, 0.56).mul(lit));
    mat.opacityNode = smoothstep(float(0.3), float(0.6), tx.r).mul(a);
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), mat, cap);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 3;
  }
  add(x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, s0: number, s1: number, col: THREE.Color): void {
    if (this.n >= this.cap) return;
    const i = this.n++ * Smoke.S, d = this.d;
    d[i] = x; d[i + 1] = y; d[i + 2] = z; d[i + 3] = vx; d[i + 4] = vy; d[i + 5] = vz;
    d[i + 6] = life; d[i + 7] = life; d[i + 8] = s0; d[i + 9] = s1; d[i + 10] = col.r; d[i + 11] = col.g; d[i + 12] = col.b;
  }
  get load(): number { return this.n / this.cap; }
  clear(): void { this.n = 0; this.mesh.count = 0; }
  update(dt: number, camQ: THREE.Quaternion): void {
    const d = this.d, S = Smoke.S, col = this.mesh.instanceColor!.array as Float32Array, al = this.alpha.array as Float32Array;
    for (let k = 0; k < this.n; k++) {
      const i = k * S; d[i + 6] -= dt;
      if (d[i + 6] <= 0) { this.n--; if (k !== this.n) d.copyWithin(i, this.n * S, this.n * S + S); k--; continue; }
      const dr = Math.exp(-2.2 * dt);
      d[i + 3] *= dr; d[i + 5] *= dr; d[i + 4] = d[i + 4] * Math.exp(-1.2 * dt) + 0.35 * dt; // billow up slowly
      d[i] += d[i + 3] * dt; d[i + 1] += d[i + 4] * dt; d[i + 2] += d[i + 5] * dt;
    }
    for (let k = 0; k < this.n; k++) {
      const i = k * S, u = 1 - d[i + 6] / d[i + 7];          // 0 → 1 over life
      const sz = d[i + 8] + (d[i + 9] - d[i + 8]) * (1 - (1 - u) * (1 - u));
      _p.set(d[i], d[i + 1], d[i + 2]); _s.set(sz, sz, sz);
      this.mesh.setMatrixAt(k, _m.compose(_p, camQ, _s));
      col[k * 3] = d[i + 10]; col[k * 3 + 1] = d[i + 11]; col[k * 3 + 2] = d[i + 12];
      al[k] = Math.min(1, u * 8) * (1 - u) ** 1.4 * 0.85;
    }
    this.mesh.count = this.n;
    this.mesh.instanceMatrix.needsUpdate = true; this.mesh.instanceColor!.needsUpdate = true; this.alpha.needsUpdate = true;
  }
}

export class Rocks {
  readonly mesh: THREE.InstancedMesh;
  private n = 0; private d: Float32Array;
  private static S = 13; // x y z vx vy vz rx ry spin life max size bounces
  /** Zenith (owner 2026-11-02 palette/rocks): optional hot rim — per-rock seconds of glow left / at launch */
  private hot: Float32Array | null = null; private hot0: Float32Array | null = null; private heat: THREE.InstancedBufferAttribute | null = null;
  /** hotHex: rocks thrown with `hot` > 0 glow with a crisp rim of this colour that cools off over those seconds */
  constructor(private cap: number, style: RenderStyle, private groundAt: (x: number, z: number) => number, slab = false, hotHex?: number) {
    this.d = new Float32Array(cap * Rocks.S);
    const g0 = slab ? new THREE.BoxGeometry(1.4, 0.22, 1.0, 1, 1, 1) : new THREE.IcosahedronGeometry(1, 0); g0.deleteAttribute('normal'); g0.deleteAttribute('uv');
    const g = mergeVertices(g0); const pos = g.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) pos.setXYZ(i, pos.getX(i) * (0.75 + ((i * 37) % 7) / 14), pos.getY(i) * (0.6 + ((i * 53) % 5) / 12), pos.getZ(i) * (0.8 + ((i * 17) % 6) / 15));
    const flat = g.toNonIndexed(); flat.computeVertexNormals(); // faceted chips read as broken rock
    const mat = style === 'cel' ? toonMaterial({ color: 0xffffff }) : new THREE.MeshStandardNodeMaterial({ color: 0xffffff, roughness: 0.95, flatShading: true });
    if (hotHex !== undefined) {
      this.hot = new Float32Array(cap); this.hot0 = new Float32Array(cap);
      this.heat = new THREE.InstancedBufferAttribute(new Float32Array(cap), 1); this.heat.setUsage(THREE.DynamicDrawUsage);
      // hard cel rim: facets turned away from the camera light up in the hot colour, plus a faint overall glow
      const rim = smoothstep(0.5, 0.56, float(1).sub(abs(dot(normalView, positionViewDirection))));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (mat as any).emissiveNode = color(hotHex).mul(rim.mul(1.6).add(0.3)).mul(instancedBufferAttribute(this.heat));
    }
    this.mesh = new THREE.InstancedMesh(flat, mat, cap);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.castShadow = false;
  }
  add(x: number, y: number, z: number, vx: number, vy: number, vz: number, size: number, life: number, col: THREE.Color, hot = 0): void {
    if (this.n >= this.cap) return;
    const k = this.n++, i = k * Rocks.S, d = this.d;
    if (this.hot && this.hot0) { this.hot[k] = hot; this.hot0[k] = Math.max(1e-3, hot); }
    d[i] = x; d[i + 1] = y; d[i + 2] = z; d[i + 3] = vx; d[i + 4] = vy; d[i + 5] = vz;
    d[i + 6] = Math.random() * 6; d[i + 7] = Math.random() * 6; d[i + 8] = (Math.random() - 0.5) * 18;
    d[i + 9] = life; d[i + 10] = life; d[i + 11] = size; d[i + 12] = 0;
    const c = this.mesh.instanceColor!.array as Float32Array; c[k * 3] = col.r; c[k * 3 + 1] = col.g; c[k * 3 + 2] = col.b;
  }
  clear(): void { this.n = 0; this.mesh.count = 0; }
  get count(): number { return this.n; }
  update(dt: number): void {
    const d = this.d, S = Rocks.S, c = this.mesh.instanceColor!.array as Float32Array;
    for (let k = 0; k < this.n; k++) {
      const i = k * S; d[i + 9] -= dt;
      if (d[i + 9] <= 0) {
        this.n--; if (k !== this.n) { d.copyWithin(i, this.n * S, this.n * S + S); c.copyWithin(k * 3, this.n * 3, this.n * 3 + 3); if (this.hot && this.hot0) { this.hot[k] = this.hot[this.n]; this.hot0[k] = this.hot0[this.n]; } }
        k--; continue;
      }
      d[i + 4] -= 24 * dt;
      d[i] += d[i + 3] * dt; d[i + 1] += d[i + 4] * dt; d[i + 2] += d[i + 5] * dt;
      const gy = this.groundAt(d[i], d[i + 2]) + d[i + 11] * 0.5;
      if (d[i + 1] < gy) { // bounce, lose energy, then settle and roll to a stop
        d[i + 1] = gy;
        if (d[i + 4] < -2 && d[i + 12] < 3) { d[i + 4] *= -0.35; d[i + 3] *= 0.55; d[i + 5] *= 0.55; d[i + 8] *= 0.6; d[i + 12]++; }
        else { d[i + 4] = 0; d[i + 3] *= Math.exp(-8 * dt); d[i + 5] *= Math.exp(-8 * dt); d[i + 8] *= Math.exp(-6 * dt); }
      }
      d[i + 6] += d[i + 8] * dt; d[i + 7] += d[i + 8] * 0.7 * dt;
    }
    for (let k = 0; k < this.n; k++) {
      const i = k * S, shrink = Math.min(1, d[i + 9] / 0.4); // sink away during the last 0.4 s
      _p.set(d[i], d[i + 1] - (1 - shrink) * d[i + 11] * 0.5, d[i + 2]); _e.set(d[i + 6], d[i + 7], 0); _q.setFromEuler(_e);
      const sz = d[i + 11] * shrink; _s.set(sz, sz, sz);
      this.mesh.setMatrixAt(k, _m.compose(_p, _q, _s));
    }
    if (this.hot && this.hot0 && this.heat) { // hot rims cool off (squared, so the glow drops fast after the first instant)
      const h = this.heat.array as Float32Array;
      for (let k = 0; k < this.n; k++) { this.hot[k] = Math.max(0, this.hot[k] - dt); const u = this.hot[k] / this.hot0[k]; h[k] = u * u; }
      this.heat.needsUpdate = true;
    }
    this.mesh.count = this.n; this.mesh.instanceMatrix.needsUpdate = true; this.mesh.instanceColor!.needsUpdate = true;
  }
}

/**
 * Cel fireball (owner 2026-11-01 explosion references: a bright dome, billowing lobes, hard cel bands — cream core,
 * yellow body, orange rim with a darker outline band, dark flame streaks — that break up as they cool). Original
 * shader work: hard-banded view-facing shading on lumpy baked spheres, heat falls with age and a noise dissolve
 * eats the lobes away at the end. Per-instance 'fx' = (age 0..1, seed, heat, 0) on the geometry.
 */
class CelFire {
  readonly mesh: THREE.InstancedMesh;
  private fx: THREE.InstancedBufferAttribute; private ft: THREE.InstancedBufferAttribute;
  private n = 0;
  private d: Float32Array;
  private static S = 16; // x y z vy s0 s1 life max seed heat delay dome r g b fire
  constructor(private cap: number) {
    this.d = new Float32Array(cap * CelFire.S);
    const g0 = new THREE.IcosahedronGeometry(1, 3); g0.deleteAttribute('normal'); g0.deleteAttribute('uv');
    const g = mergeVertices(g0), pos = g.attributes.position as THREE.BufferAttribute, v = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) { // billowy lobes baked into the shape
      v.fromBufferAttribute(pos, i); const n = v.clone().normalize();
      const l = 1 + 0.14 * Math.sin(n.x * 6.1 + 1) * Math.sin(n.y * 5.3 + 2) * Math.sin(n.z * 5.7) + 0.06 * Math.sin(n.x * 13 + n.z * 11);
      pos.setXYZ(i, v.x * l, v.y * l, v.z * l);
    }
    g.computeVertexNormals();
    this.fx = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4); this.fx.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('fx', this.fx);
    this.ft = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3); this.ft.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('ft', this.ft);
    const m = new THREE.MeshBasicNodeMaterial({ transparent: false, alphaTest: 0.5, fog: false });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const F = attribute('fx', 'vec4') as any;
    const age = F.x, seed = F.y, heat = F.z;
    const facing = abs(dot(normalView, positionViewDirection));                 // 1 at the centre, 0 at the silhouette
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const churn = (mx_noise_float(positionGeometry.mul(2.2).add(vec3(seed.mul(7.0), time.mul(1.5), seed))) as any).mul(0.5).add(0.5);
    const k = facing.mul(0.85).add(churn.mul(0.3)).sub(age.mul(0.55)).add(heat.mul(0.15));
    // owner 2026-11-01 (genre reference): explosions take the colour of the attack — dark rim → attack colour → bright
    // attack-tinted body → white-hot core. Orange tints still read as fire; blue/violet energy attacks read as energy domes.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const T = attribute('ft', 'vec3') as any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let col: any = mix(T.mul(0.5), T.mul(0.9), smoothstep(0.18, 0.22, k));               // dark rim band → attack colour
    col = mix(col, T.mul(1.05), smoothstep(0.36, 0.4, k));
    col = mix(col, mix(T, vec3(1, 1, 1), 0.3), smoothstep(0.52, 0.56, k));          // bright tinted body (stays saturated)
    col = mix(col, color(0xfff6d0), smoothstep(0.9, 0.93, k).mul(blastFlashK));       // cream-white core (small hot centre; a11y-scaled)
    // dark flame streaks: thin bands where a stretched noise crosses zero
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const st = (mx_noise_float(vec3(positionGeometry.x.mul(1.6), positionGeometry.y.mul(4.5), positionGeometry.z.mul(1.6)).add(seed.mul(3.0))) as any);
    col = mix(col, T.mul(0.45), smoothstep(0.07, 0.03, st.abs()).mul(smoothstep(0.3, 0.6, age)).mul(0.6));
    // owner 2026-11-01: 'more fiery… multicolour… orange, yellow, red… in a fiery swirl'. A twisting noise field (rotated
    // around the vertical axis more the higher you go, and over time) drives hard red/orange/yellow/pale bands; F.w
    // blends between this fire palette (explosions) and the attack-tinted palette above (Spirit Bolt pops keep their tint).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const G: any = positionGeometry, tw = G.y.mul(2.4).add(time.mul(2.6)).add(seed.mul(6.0)), ct = cos(tw), stw = sin(tw);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const swirl = (mx_noise_float(vec3(G.x.mul(ct).sub(G.z.mul(stw)).mul(2.3), G.y.mul(1.6).sub(time.mul(0.8)), G.x.mul(stw).add(G.z.mul(ct)).mul(2.3))) as any).mul(0.5).add(0.5);
    const kf = k.mul(0.7).add(swirl.mul(0.5)).sub(0.16);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let fire: any = mix(color(0x7a0e06), color(0xd8240c), smoothstep(0.16, 0.2, kf));  // dark ember edge -> red
    fire = mix(fire, color(0xff5a12), smoothstep(0.34, 0.38, kf));                      // orange
    fire = mix(fire, color(0xffa018), smoothstep(0.5, 0.54, kf));                       // amber
    fire = mix(fire, color(0xffd83a), smoothstep(0.64, 0.68, kf));                      // yellow
    fire = mix(fire, color(0xfff3b0), smoothstep(0.84, 0.88, kf).mul(blastFlashK));    // pale hot core (a11y-scaled)
    fire = mix(fire, color(0x3a1408), smoothstep(0.07, 0.03, st.abs()).mul(smoothstep(0.35, 0.65, age)).mul(0.6)); // soot streaks as it cools
    col = mix(col, fire, F.w);
    m.colorNode = col.mul(blastFlashK.mul(0.4).add(0.75)); // 1.15 as authored; Reduce flashing calms the fireball pop
    // cooling dissolve: lobes break up from the edges inward as age → 1
    m.opacityNode = churn.add(facing.mul(0.25)).sub(age.mul(age).mul(1.35)).add(0.55);
    this.mesh = new THREE.InstancedMesh(g, m, cap);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 2;
  }
  add(x: number, y: number, z: number, vy: number, s0: number, s1: number, life: number, heat: number, delay: number, dome = 0, tint = 0xffa030, fire = 1): void {
    if (this.n >= this.cap) return;
    const i = this.n++ * CelFire.S, d = this.d;
    // saturate the attack colour (pale tints like 0xffb070 would otherwise wash out to white)
    _t.setHex(tint); const hsl = { h: 0, s: 0, l: 0 }; _t.getHSL(hsl); // yellows/golds keep their lightness (darkening a yellow reads orange-red); other hues deepen a little
    const yellow = hsl.h > 0.1 && hsl.h < 0.2; _t.setHSL(hsl.h, Math.max(0.85, hsl.s), yellow ? Math.max(0.55, Math.min(0.62, hsl.l)) : Math.min(0.55, Math.max(0.42, hsl.l * 0.7)));
    d[i + 12] = _t.r; d[i + 13] = _t.g; d[i + 14] = _t.b; d[i + 15] = fire;
    d[i] = x; d[i + 1] = y; d[i + 2] = z; d[i + 3] = vy; d[i + 4] = s0; d[i + 5] = s1; d[i + 6] = life + delay; d[i + 7] = life;
    d[i + 8] = Math.random(); d[i + 9] = heat; d[i + 10] = delay; d[i + 11] = dome;
  }
  get load(): number { return this.n / this.cap; }
  clear(): void { this.n = 0; this.mesh.count = 0; }
  update(dt: number): void {
    const d = this.d, S = CelFire.S, F = this.fx.array as Float32Array, FT = this.ft.array as Float32Array;
    for (let k = 0; k < this.n; k++) {
      const i = k * S; d[i + 6] -= dt;
      if (d[i + 6] <= 0) { this.n--; if (k !== this.n) d.copyWithin(i, this.n * S, this.n * S + S); k--; continue; }
      if (d[i + 6] < d[i + 7]) { d[i + 1] += d[i + 3] * dt; d[i + 3] *= Math.exp(-2.5 * dt); }
    }
    let vis = 0;
    for (let k = 0; k < this.n; k++) {
      const i = k * S; if (d[i + 6] > d[i + 7]) continue; // still delayed
      const u = 1 - d[i + 6] / d[i + 7], grow = 1 - (1 - u) ** 3;
      const sc = d[i + 4] + (d[i + 5] - d[i + 4]) * grow;
      _p.set(d[i], d[i + 1], d[i + 2]); _s.set(sc, sc * (d[i + 11] ? 0.78 : 1), sc); _q.identity();
      this.mesh.setMatrixAt(vis, _m.compose(_p, _q, _s));
      F[vis * 4] = u; F[vis * 4 + 1] = d[i + 8]; F[vis * 4 + 2] = d[i + 9]; F[vis * 4 + 3] = d[i + 15];
      FT[vis * 3] = d[i + 12]; FT[vis * 3 + 1] = d[i + 13]; FT[vis * 3 + 2] = d[i + 14];
      vis++;
    }
    this.mesh.count = vis; this.mesh.instanceMatrix.needsUpdate = true; this.fx.needsUpdate = true; this.ft.needsUpdate = true;
  }
}

/**
 * Pulse Nova shockwave (owner 2026-11-01: 'fix the pulse nova animations'): an expanding translucent energy dome with a
 * bright fresnel rim, plus a flat cel shock ring racing along the ground with a hard white leading edge and a fading
 * cyan trail, and a thinner echo ring. Per-instance 'nv' = (age 0..1, kind 0 dome / 1 ring, gold 0/1, 0).
 */
class NovaWaves {
  // owner 2026-11-01 (reference at 49:20): a HUGE energy sphere around the hero — white-hot core, glowing swirling shell,
  // crescent energy arcs sweeping round it — that swells to the push radius, holds a beat, then fades; plus the racing
  // ground shock ring and its echo. Per-instance 'nv' = (fade 0..1, kind, gold 0/1, seed).
  readonly dome: THREE.InstancedMesh; readonly ring: THREE.InstancedMesh; readonly arcs: THREE.InstancedMesh;
  private dA: THREE.InstancedBufferAttribute; private rA: THREE.InstancedBufferAttribute; private aA: THREE.InstancedBufferAttribute;
  private items: { x: number; y: number; z: number; r: number; t: number; life: number; delay: number; kind: 0 | 1 | 2; gold: number; w: number; ax: THREE.Vector3; spin: number }[] = [];
  constructor(private cap: number) {
    const mk = (geo: THREE.BufferGeometry, kind: 0 | 1 | 2) => {
      const attr = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4); attr.setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute('nv', attr);
      const additive = kind !== 0;
      const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending, side: THREE.DoubleSide, fog: false });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const N = attribute('nv', 'vec4') as any, fade = N.x, gold = N.z;
      const shell = mix(color(0x0e4fd6), color(0xe08a10), gold), bright = mix(color(0x5cc8ff), color(0xffd27a), gold);
      if (kind === 1) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const v = (uv() as any).y;
        const lead = smoothstep(0.78, 0.9, v), body = smoothstep(0.0, 0.55, v);
        m.colorNode = mix(shell.mul(body.mul(1.5)), bright.mul(blastFlashK.mul(1.4).add(0.8)), lead); // 2.2 as authored (a11y-scaled)
        m.opacityNode = body.mul(float(1).sub(fade).pow(1.3));
      } else if (kind === 2) {
        m.colorNode = bright.mul(blastFlashK.mul(1.3).add(0.7)); // 2.0 as authored (a11y-scaled)
        m.opacityNode = float(1).sub(fade).pow(1.2);
      } else {
        const facing = abs(dot(normalView, positionViewDirection)), fres = facing.oneMinus();
        const swirl = mx_noise_float(positionGeometry.mul(2.6).add(vec3(time.mul(2.2), time.mul(-1.4), N.w.mul(9)))).mul(0.5).add(0.5);
        const streak = smoothstep(0.62, 0.7, swirl);
        let c = mix(shell, bright, fres.pow(1.4).max(streak.mul(0.7)));
        c = mix(c, color(0xffffff), smoothstep(0.86, 0.99, facing).mul(blastFlashK.mul(0.7))); // smaller white-hot core (a11y-scaled)
        m.colorNode = c.mul(1.05);
        m.opacityNode = float(0.42).add(fres.mul(0.45)).add(streak.mul(0.2)).add(smoothstep(0.86, 0.99, facing).mul(0.25)).min(0.95).mul(float(1).sub(fade).pow(1.5));
      }
      const im = new THREE.InstancedMesh(geo, m, cap); im.frustumCulled = false; im.count = 0; im.renderOrder = kind === 0 ? 3 : 4;
      return { im, attr };
    };
    const ringGeo = new THREE.RingGeometry(0.72, 1, 96, 1).rotateX(-Math.PI / 2);
    const pos = ringGeo.attributes.position as THREE.BufferAttribute, uvA = ringGeo.attributes.uv as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) { const r = Math.hypot(pos.getX(i), pos.getZ(i)); uvA.setXY(i, 0, (r - 0.72) / 0.28); }
    const d = mk(new THREE.SphereGeometry(1, 48, 28), 0), r = mk(ringGeo, 1), a = mk(new THREE.TorusGeometry(1, 0.022, 6, 64, Math.PI * 1.1), 2);
    this.dome = d.im; this.dA = d.attr; this.ring = r.im; this.rA = r.attr; this.arcs = a.im; this.aA = a.attr;
  }
  add(x: number, y: number, z: number, r: number, gold: boolean): void {
    if (this.items.length > this.cap - 6) return;
    const g = gold ? 1 : 0, Z = new THREE.Vector3();
    this.items.push({ x, y, z, r, t: 0, life: 0.7, delay: 0, kind: 0, gold: g, w: Math.random(), ax: Z, spin: 0 });
    this.items.push({ x, y, z, r: r * 1.08, t: 0, life: 0.45, delay: 0.05, kind: 1, gold: g, w: 1, ax: Z, spin: 0 });
    this.items.push({ x, y, z, r: r * 0.95, t: 0, life: 0.42, delay: 0.17, kind: 1, gold: g, w: 0.55, ax: Z, spin: 0 }); // echo ring
    for (let k = 0; k < 3; k++) { // crescent arcs sweeping around the sphere on tilted axes
      const ax = new THREE.Vector3(Math.random() - 0.5, 0.6 + Math.random() * 0.5, Math.random() - 0.5).normalize();
      this.items.push({ x, y, z, r: r * (1.02 + k * 0.05), t: 0, life: 0.55, delay: 0.04 * k, kind: 2, gold: g, w: Math.random() * 6.28, ax, spin: (k % 2 ? 1 : -1) * (7 + Math.random() * 4) });
    }
  }
  clear(): void { this.items.length = 0; this.dome.count = 0; this.ring.count = 0; this.arcs.count = 0; }
  update(dt: number): void {
    let nd = 0, nr = 0, na = 0; const DA = this.dA.array as Float32Array, RA = this.rA.array as Float32Array, AA = this.aA.array as Float32Array;
    for (let k = this.items.length - 1; k >= 0; k--) { const it = this.items[k]; it.t += dt; if (it.t > it.life + it.delay) this.items.splice(k, 1); }
    for (const it of this.items) {
      const tt = it.t - it.delay; if (tt < 0) continue;
      const u = Math.min(1, tt / it.life);
      if (it.kind === 1) {
        const grow = 1 - (1 - u) ** 2.4, R = it.r * (0.15 + 0.85 * grow); _p.set(it.x, 0.12 + 0.01 * nr, it.z); _q.identity(); _s.set(R, 1, R);
        this.ring.setMatrixAt(nr, _m.compose(_p, _q, _s)); RA[nr * 4] = u + (1 - it.w) * 0.35; RA[nr * 4 + 2] = it.gold; nr++;
      } else if (it.kind === 0) {
        // swell fast (0.12 s), hold with a throb, fade over the last 40 %
        const g = Math.min(1, tt / 0.12), R = it.r * (0.2 + 0.8 * (1 - (1 - g) ** 3)) * (1 + Math.sin(tt * 40) * 0.025);
        _p.set(it.x, it.r * 0.18, it.z); _q.identity(); _s.set(R, R * 0.92, R);
        this.dome.setMatrixAt(nd, _m.compose(_p, _q, _s)); DA[nd * 4] = Math.max(0, (u - 0.6) / 0.4); DA[nd * 4 + 2] = it.gold; DA[nd * 4 + 3] = it.w; nd++;
      } else {
        const g = Math.min(1, tt / 0.12), R = it.r * (0.3 + 0.7 * g);
        _q.setFromAxisAngle(it.ax, it.w + tt * it.spin); _p.set(it.x, it.r * 0.18, it.z); _s.set(R, R, R);
        this.arcs.setMatrixAt(na, _m.compose(_p, _q, _s)); AA[na * 4] = Math.max(0, (u - 0.5) / 0.5); AA[na * 4 + 2] = it.gold; na++;
      }
    }
    this.dome.count = nd; this.ring.count = nr; this.arcs.count = na;
    for (const m of [this.dome, this.ring, this.arcs]) m.instanceMatrix.needsUpdate = true;
    this.dA.needsUpdate = true; this.rA.needsUpdate = true; this.aA.needsUpdate = true;
  }
}

/** Ink shards (reference: impacts spray jagged dark shards outward at speed). Thin elongated tetra shards stretched
 *  along their velocity, dark navy/black with a tint, fast and short-lived. */
class InkShards {
  readonly mesh: THREE.InstancedMesh;
  private n = 0; private d: Float32Array; private static S = 10; // x y z vx vy vz life max len w
  constructor(private cap: number) {
    this.d = new Float32Array(cap * InkShards.S);
    const g = new THREE.ConeGeometry(0.5, 1, 3, 1).rotateX(Math.PI / 2); // points along +Z
    const m = new THREE.MeshBasicNodeMaterial({ fog: false });
    this.mesh = new THREE.InstancedMesh(g, m, cap);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    this.mesh.frustumCulled = false; this.mesh.count = 0;
  }
  add(x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, len: number, w: number, col: THREE.Color): void {
    if (this.n >= this.cap) return;
    const k = this.n++, i = k * InkShards.S, d = this.d;
    d[i] = x; d[i + 1] = y; d[i + 2] = z; d[i + 3] = vx; d[i + 4] = vy; d[i + 5] = vz; d[i + 6] = life; d[i + 7] = life; d[i + 8] = len; d[i + 9] = w;
    const c = this.mesh.instanceColor!.array as Float32Array; c[k * 3] = col.r; c[k * 3 + 1] = col.g; c[k * 3 + 2] = col.b;
  }
  clear(): void { this.n = 0; this.mesh.count = 0; }
  update(dt: number): void {
    const d = this.d, S = InkShards.S, c = this.mesh.instanceColor!.array as Float32Array;
    for (let k = 0; k < this.n; k++) {
      const i = k * S; d[i + 6] -= dt;
      if (d[i + 6] <= 0) { this.n--; if (k !== this.n) { d.copyWithin(i, this.n * S, this.n * S + S); c.copyWithin(k * 3, this.n * 3, this.n * 3 + 3); } k--; continue; }
      const dr = Math.exp(-3 * dt); d[i + 3] *= dr; d[i + 5] *= dr; d[i + 4] = d[i + 4] * dr - 9 * dt;
      d[i] += d[i + 3] * dt; d[i + 1] = Math.max(0.05, d[i + 1] + d[i + 4] * dt); d[i + 2] += d[i + 5] * dt;
    }
    const v = new THREE.Vector3(), f = new THREE.Vector3(0, 0, 1);
    for (let k = 0; k < this.n; k++) {
      const i = k * S, u = d[i + 6] / d[i + 7];
      v.set(d[i + 3], d[i + 4], d[i + 5]); const sp = v.length() || 1; v.divideScalar(sp);
      _q.setFromUnitVectors(f, v);
      const L = d[i + 8] * (0.4 + Math.min(1.6, sp / 14)) * (0.3 + 0.7 * u), W = d[i + 9] * u;
      _p.set(d[i], d[i + 1], d[i + 2]); _s.set(W, W * 0.4, L);
      this.mesh.setMatrixAt(k, _m.compose(_p, _q, _s));
    }
    this.mesh.count = this.n; this.mesh.instanceMatrix.needsUpdate = true; this.mesh.instanceColor!.needsUpdate = true;
  }
}

const SMOKE_DARK = new THREE.Color(0x5c5048), SMOKE_MID = new THREE.Color(0x857462);
// dust / turf slabs / rock grit follow the map's debris palette (debrisLook.ts; owner 2026-11-02: no brown dirt on snow)
const DUST = DEBRIS.dust;
const _c = new THREE.Color();
/** one fire lobe as spawned (CelFire motion: y += vy·dt with vy·e^(-2.5t); size s0→s1 eased over life, after delay) */
export interface CloudLobe { x: number; y: number; z: number; vy: number; s0: number; s1: number; life: number; delay: number }

export class BlastFx {
  private smoke: Smoke; private rocks: Rocks; private fire: CelFire; private nova = new NovaWaves(24); private ink = new InkShards(260); private slabs: Rocks;
  constructor(scene: THREE.Scene, quality: string, style: RenderStyle, groundAt: (x: number, z: number) => number) {
    const q = quality === 'low' ? 0.4 : quality === 'medium' ? 0.7 : 1;
    this.smoke = new Smoke(Math.round(420 * q)); this.rocks = new Rocks(Math.round(600 * q), style, groundAt); this.fire = new CelFire(Math.round(220 * q)); this.slabs = new Rocks(Math.round(90 * q), style, groundAt, true);
    scene.add(this.smoke.mesh, this.rocks.mesh, this.fire.mesh, this.nova.dome, this.nova.ring, this.nova.arcs, this.ink.mesh, this.slabs.mesh);
  }
  /** r = blast radius (m). onGround: the blast touched the ground (craters / dust); big: boss-scale */
  /** ownColour: burn in the tint instead of the shared orange fire swirl (the green Rift Mortar) */
  /** proc = grow the fireball procedurally (procCloud) and return its lobes (Rift Mortar's electricity crawls over them) */
  blast(x: number, y: number, z: number, r: number, big: boolean, tint?: number, ownColour = false, proc = false): CloudLobe[] | null {
    const fmix = ownColour ? 0.08 : 1;
    const thin = Math.max(0.25, 1 - Math.max(CROWD.busy - 0.3, 0) * 1.2) * (this.smoke.load > 0.75 ? 0.4 : 1);
    const gy = Math.max(0.2, y * 0.4);
    // 0) cel fireball: a ground dome that swells fast, then billowing lobes around/above it (staggered), all cooling
    //    from cream-yellow to orange-red and breaking up; smoke and rocks below carry the aftermath
    const fthin = this.fire.load > 0.8 ? 0.35 : thin;
    const dome = r * (big ? 1.05 : 0.95);
    const tc = tint ?? 0xffa030;
    let lobes: CloudLobe[] | null = null;
    if (proc) lobes = this.procCloud(x, gy * 0.3, z, r, tc, fmix, big, fthin); // Rift Mortar: a freshly grown cloud every blast
    else {
    this.fire.add(x, gy * 0.3, z, 0, dome * 0.35, dome * 1.15, (big ? 0.85 : 0.6), 1, 0, 1, tc, fmix);
    const nl = Math.round(Math.min(12, 4 + r * 1.3) * fthin * (big ? 1.4 : 1));
    for (let k = 0; k < nl; k++) {
      const a = (k / nl) * 6.283 + Math.random() * 0.5, el = Math.random() * 1.1, R = dome * (0.55 + Math.random() * 0.45);
      const lx = x + Math.cos(a) * Math.cos(el) * R, lz = z + Math.sin(a) * Math.cos(el) * R, ly = gy * 0.3 + Math.sin(el) * R * 0.8;
      const ls = dome * (0.3 + Math.random() * 0.25);
      this.fire.add(lx, ly, lz, 1.2 + Math.random() * r * 0.4, ls * 0.4, ls * (1.3 + Math.random() * 0.4), 0.45 + Math.random() * 0.35, 0.6, 0.03 + Math.random() * 0.12, 0, tc, fmix);
    }
    }
    // 0a) owner 2026-11-01: 'small particles of rocks flying outward… upward' — a spray of small pebbles and grit
    {
      const ng = Math.round(Math.min(30, 10 + r * 3.5) * Math.max(0.4, thin));
      for (let k = 0; k < ng; k++) {
        const a = Math.random() * 6.283, out = r * (1.2 + Math.random() * 3.2), up = 6 + Math.random() * (9 + r * 2.2);
        this.rocks.add(x + Math.cos(a) * r * 0.25, gy + 0.3, z + Math.sin(a) * r * 0.25, Math.cos(a) * out, up, Math.sin(a) * out,
          (r < 3.5 ? 0.03 + Math.random() * 0.05 : 0.05 + Math.random() * 0.09), 1.0 + Math.random() * 1.0, DEBRIS.grit[k % DEBRIS.grit.length]);
      }
    }
    // 0b) ink shards: a fast radial spray of dark jagged shards (bigger blasts only)
    if (r >= 2.5) {
      const ni = Math.round(Math.min(22, 6 + r * 2.2) * thin * (big ? 1.5 : 1));
      for (let k = 0; k < ni; k++) {
        const a = Math.random() * 6.283, el = Math.random() * 0.7, sp = 14 + Math.random() * 16 + r * 2;
        _c.setHex(Math.random() < 0.7 ? 0x0e1222 : 0x2a1a3a);
        this.ink.add(x, gy + 0.4, z, Math.cos(a) * Math.cos(el) * sp, Math.sin(el) * sp * 0.8, Math.sin(a) * Math.cos(el) * sp, 0.35 + Math.random() * 0.3, 0.9 + Math.random() * 1.4, 0.18 + Math.random() * 0.2, _c);
      }
    }
    // 0c) big blasts tear up the ground: flat slabs of turf/dirt flung high, tumbling; and a towering brown dust curtain
    if (r >= 3.5) {
      const ns = Math.round(Math.min(9, 2 + r * 0.6) * Math.max(0.4, thin) * (big ? 1.5 : 1));
      for (let k = 0; k < ns; k++) {
        const a = Math.random() * 6.283, out = r * (0.9 + Math.random() * 1.4);
        this.slabs.add(x + Math.cos(a) * r * 0.4, gy + 0.3, z + Math.sin(a) * r * 0.4, Math.cos(a) * out, 9 + Math.random() * (6 + r), Math.sin(a) * out,
          0.5 + Math.random() * 0.6 + r * 0.05, 2.4 + Math.random() * 1.2, DEBRIS.turf[k % DEBRIS.turf.length]);
      }
      const nc = Math.round(Math.min(14, 4 + r) * thin * (big ? 1.4 : 1));
      for (let k = 0; k < nc; k++) {
        const a = Math.random() * 6.283, d = r * (0.3 + Math.random() * 0.6);
        _c.copy(DUST).lerp(SMOKE_MID, Math.random() * 0.4).multiplyScalar(0.85 + Math.random() * 0.2);
        this.smoke.add(x + Math.cos(a) * d, gy + 0.5, z + Math.sin(a) * d, Math.cos(a) * 1.5, 3 + Math.random() * (2 + r * 0.5), Math.sin(a) * 1.5,
          2.2 + Math.random() * 1.4, r * 0.4 + 0.8, r * 0.9 + 2 + Math.random() * r * 0.3, _c);
      }
    }
    // 1) billowing smoke column: dark core puffs low, lighter puffs rising above
    const ns = Math.round(Math.min(18, 4 + r * 1.6) * thin * (big ? 1.6 : 1));
    for (let k = 0; k < ns; k++) {
      const a = Math.random() * 6.283, d = Math.random() * r * 0.5, up = k / Math.max(1, ns - 1);
      _c.copy(up < 0.5 ? SMOKE_DARK : SMOKE_MID).lerp(DUST, Math.random() * 0.25);
      if (tint !== undefined) _c.lerp(_t.setHex(tint), 0.08);
      this.smoke.add(x + Math.cos(a) * d, gy + up * r * 0.5, z + Math.sin(a) * d, Math.cos(a) * r * 0.5, 0.6 + up * r * 0.7 + Math.random(), Math.sin(a) * r * 0.5,
        1.6 + Math.random() * 1.2 + (big ? 1 : 0), r * 0.55 + 0.6, r * 1.15 + 1.4 + Math.random() * r * 0.4, _c);
    }
    // 2) dust skirt rolling outward along the ground
    const nd = Math.round(Math.min(16, 6 + r * 1.4) * thin);
    for (let k = 0; k < nd; k++) {
      const a = (k / nd) * 6.283 + Math.random() * 0.3, sp = r * (2.2 + Math.random() * 1.2);
      _c.copy(DUST).multiplyScalar(0.9 + Math.random() * 0.2);
      this.smoke.add(x + Math.cos(a) * r * 0.4, 0.35, z + Math.sin(a) * r * 0.4, Math.cos(a) * sp, 0.25, Math.sin(a) * sp, 1.1 + Math.random() * 0.8, r * 0.4 + 0.5, r * 0.7 + 1.2, _c);
    }
    // 3) rocks flying everywhere: chunks thrown up and out, a few big ones
    const nr = Math.round(Math.min(26, 5 + r * 2.4) * Math.max(0.35, thin) * (big ? 1.5 : 1));
    for (let k = 0; k < nr; k++) {
      const a = Math.random() * 6.283, out = r * (1.6 + Math.random() * 2.4), upV = 7 + Math.random() * (6 + r * 1.6);
      // owner 2026-11-02: rocks from enemy-death explosions (small blasts, r < 3.5) are pebble-sized, no big chunks;
      // skill-scale blasts keep their chunks
      const size = r < 3.5 ? 0.04 + Math.random() * 0.07 : (0.12 + Math.random() * 0.22) * (Math.random() < 0.15 ? 2.2 : 1) * (big ? 1.3 : 1);
      this.rocks.add(x + Math.cos(a) * r * 0.3, gy + 0.3, z + Math.sin(a) * r * 0.3, Math.cos(a) * out, upV, Math.sin(a) * out, size, 2.2 + Math.random() * 1.6, DEBRIS.grit[k % DEBRIS.grit.length]);
    }
    return lobes;
  }
  /** Rift Mortar (owner 2026-11-02: 'the explosion should be procedurally generated'): a new cloud shape every blast.
   *  A hot dome, then 8–16+ lobes that each bud off a random earlier lobe (branching growth → lumpy, uneven clouds),
   *  leaning in a random direction, sometimes with a rising plume column; dark smoke puffs seeded on random lobes.
   *  Returns the lobes (spawn state) so effects can follow the cloud's surface. */
  procCloud(x: number, y0: number, z: number, r: number, tint: number, fmix: number, big: boolean, thin: number): CloudLobe[] {
    const L: CloudLobe[] = [], R = Math.random;
    const push = (lx: number, ly: number, lz: number, vy: number, s0: number, s1: number, life: number, heat: number, delay: number, dome: number) => {
      L.push({ x: lx, y: ly, z: lz, vy, s0, s1, life, delay }); this.fire.add(lx, ly, lz, vy, s0, s1, life, heat, delay, dome, tint, fmix);
    };
    const lean = R() * 6.283, leanK = 0.15 + R() * 0.5, ldx = Math.cos(lean) * leanK, ldz = Math.sin(lean) * leanK;
    const dome = r * (0.8 + R() * 0.35);
    push(x, y0, z, 0, dome * 0.3, dome * (0.95 + R() * 0.3), (big ? 0.8 : 0.6) + R() * 0.2, 1, 0, 1);
    const n = Math.round((8 + R() * 8 + r * 0.7) * thin);
    for (let k = 0; k < n; k++) {
      const p = L[Math.floor(R() * L.length)];
      let dx = R() * 2 - 1 + ldx, dy = R() * 1.2 + 0.15, dz = R() * 2 - 1 + ldz; const dl = Math.hypot(dx, dy, dz) || 1; dx /= dl; dy /= dl; dz /= dl;
      const dist = p.s1 * (0.5 + R() * 0.4), s1 = Math.max(r * 0.16, p.s1 * (0.45 + R() * 0.4));
      push(p.x + dx * dist, Math.max(y0 * 0.5, p.y + dy * dist), p.z + dz * dist, 0.6 + R() * (1.2 + r * 0.45), s1 * 0.35, s1, 0.45 + R() * 0.45, 0.45 + R() * 0.5, p.delay + 0.015 + R() * 0.07, 0);
    }
    if (R() < 0.45) { // a rising plume column
      let py = y0 + dome * 0.6, ps = dome * 0.45; const m = 3 + Math.floor(R() * 3);
      for (let k = 0; k < m; k++) { py += ps * 0.8; ps *= 0.8; push(x + ldx * py * 0.5 + (R() - 0.5) * 0.4, py, z + ldz * py * 0.5 + (R() - 0.5) * 0.4, 1.5 + R() * 2, ps * 0.3, ps, 0.5 + R() * 0.3, 0.6, 0.06 + k * 0.04, 0); }
    }
    const ns = Math.round((6 + R() * 6) * thin * (this.smoke.load > 0.75 ? 0.5 : 1)), tc = new THREE.Color(tint);
    for (let k = 0; k < ns; k++) {
      const p = L[Math.floor(R() * L.length)];
      _c.copy(SMOKE_DARK).lerp(SMOKE_MID, R() * 0.5).lerp(tc, 0.12);
      this.smoke.add(p.x + (R() - 0.5) * p.s1, p.y + p.s1 * 0.3, p.z + (R() - 0.5) * p.s1, ldx * 2 + (R() - 0.5) * 1.5, 0.6 + R() * 1.6, ldz * 2 + (R() - 0.5) * 1.5, 0.9 + R() * 0.8, p.s1 * 0.5, p.s1 * (1.2 + R() * 0.8), _c);
    }
    return L;
  }
  /** Rift Mortar burst (P1 'Ember Core Cannon' spec): a reduced blast() from the same pools — a hot dome, 3–5 staggered
   *  fire lobes (0.10–0.35 s), a low dust ring, a few rocks, then a little dark smoke that clears quickly. tint = sphere
   *  shell colour; the fire keeps its hot structure. */
  mortarBurst(x: number, z: number, r: number, tint: number): void {
    const thin = Math.max(0.35, 1 - Math.max(CROWD.busy - 0.3, 0) * 1.2) * (this.smoke.load > 0.75 ? 0.5 : 1);
    const dome = r * 0.6;
    // fire-hued tints keep the orange fire swirl; other colours (the green Rift Mortar, Surge red/blue) burn in their own colour
    _c.setHex(tint); const hsl = { h: 0, s: 0, l: 0 }; _c.getHSL(hsl); const fm = hsl.h < 0.15 && hsl.h > 0.02 ? 1 : 0.1;
    this.fire.add(x, 0.15, z, 0, dome * 0.4, dome, 0.42, 1, 0, 1, tint, fm);
    const nl = this.fire.load > 0.8 ? 3 : 3 + Math.floor(Math.random() * 3);
    for (let k = 0; k < nl; k++) {
      const a = (k / nl) * 6.283 + Math.random() * 0.8, R = dome * (0.55 + Math.random() * 0.35), ls = dome * (0.35 + Math.random() * 0.2);
      this.fire.add(x + Math.cos(a) * R, 0.3 + Math.random() * dome * 0.5, z + Math.sin(a) * R, 1 + Math.random() * 1.5, ls * 0.4, ls * 1.3, 0.3 + Math.random() * 0.15, 0.7, 0.08 + Math.random() * 0.06, 0, tint, fm);
    }
    const nd = Math.round(10 * thin);
    for (let k = 0; k < nd; k++) {
      const a = (k / nd) * 6.283 + Math.random() * 0.3, sp = r * (2.4 + Math.random());
      _c.copy(DUST).multiplyScalar(0.9 + Math.random() * 0.2);
      this.smoke.add(x + Math.cos(a) * r * 0.3, 0.25, z + Math.sin(a) * r * 0.3, Math.cos(a) * sp, 0.15, Math.sin(a) * sp, 0.6 + Math.random() * 0.3, r * 0.25 + 0.3, r * 0.45 + 0.7, _c);
    }
    const nr = Math.round((5 + r) * Math.max(0.4, thin));
    for (let k = 0; k < nr; k++) {
      const a = Math.random() * 6.283, out = r * (1.2 + Math.random() * 1.6);
      this.rocks.add(x + Math.cos(a) * r * 0.2, 0.4, z + Math.sin(a) * r * 0.2, Math.cos(a) * out, 6 + Math.random() * 6, Math.sin(a) * out, 0.08 + Math.random() * 0.14, 1 + Math.random() * 0.8, DEBRIS.grit[k % DEBRIS.grit.length]);
    }
    const ns = Math.round(5 * thin);
    for (let k = 0; k < ns; k++) {
      const a = Math.random() * 6.283, d = Math.random() * r * 0.4;
      _c.copy(SMOKE_DARK).lerp(SMOKE_MID, Math.random() * 0.4);
      this.smoke.add(x + Math.cos(a) * d, 0.5 + k * 0.25, z + Math.sin(a) * d, Math.cos(a) * r * 0.4, 1 + Math.random() * 1.5, Math.sin(a) * r * 0.4, 0.55 + Math.random() * 0.25, r * 0.35 + 0.4, r * 0.6 + 0.8, _c);
    }
  }
  /** Spirit Bolt hit (owner 2026-11-01: 'a small little explosion when it hits enemies'): a tiny cel pop — one quick
   *  bright lobe plus two satellites in the bolt's colour, a pinch of sparks, no smoke — light enough for rapid fire. */
  /** Spirit Bolt hit (owner 2026-11-02: 'a single smaller ball of fire, about the same size as the energy blast'): one cel
   *  fireball that swells to r (m) and burns out; no side puffs, ink or smoke */
  popBall(x: number, y: number, z: number, r: number, tint: number): void {
    if (this.fire.load > 0.9) return;
    this.fire.add(x, y, z, 0, r * 0.35, r, 0.2, 1, 0, 0, tint, 0);
  }
  pop(x: number, y: number, z: number, tint: number): void {
    if (this.fire.load > 0.85) return;
    this.fire.add(x, y, z, 0, 0.15, 0.85, 0.22, 1, 0, 0, tint, 0);
    for (let k = 0; k < 2; k++) { const a = Math.random() * 6.283; this.fire.add(x + Math.cos(a) * 0.45, y + 0.2, z + Math.sin(a) * 0.45, 1.5, 0.1, 0.5, 0.2, 0.7, 0.03, 0, tint, 0); }
    for (let k = 0; k < 4; k++) { const a = Math.random() * 6.283, sp = 7 + Math.random() * 6; _c.setHex(0x0e1630); this.ink.add(x, y, z, Math.cos(a) * sp, 1 + Math.random() * 3, Math.sin(a) * sp, 0.18 + Math.random() * 0.1, 0.35, 0.07, _c); }
    // reference study: the puffs settle into a little dark smoke (cheap: one or two puffs, skipped when the pool is busy)
    if (this.smoke.load < 0.6) for (let k = 0; k < 2; k++) { _c.copy(SMOKE_DARK).multiplyScalar(0.7); this.smoke.add(x + (Math.random() - 0.5) * 0.5, y, z + (Math.random() - 0.5) * 0.5, 0, 0.8, 0, 0.7 + Math.random() * 0.3, 0.3, 1.1, _c); }
  }
  /** Rock chips flung sideways out of a carved trench (Nova Lance): n chips from (x,z), thrown along side normal (nx,nz). */
  chips(x: number, z: number, nx: number, nz: number, n: number): void {
    for (let k = 0; k < n; k++) {
      const sp = 3 + Math.random() * 5;
      this.rocks.add(x, 0.2, z, nx * sp + (Math.random() - 0.5) * 2, 4 + Math.random() * 5, nz * sp + (Math.random() - 0.5) * 2, 0.08 + Math.random() * 0.14, 1.4 + Math.random() * 0.8, DEBRIS.grit[k % DEBRIS.grit.length]);
    }
  }
  /** MAP-DESTRUCTION (owner 2026-11-02): n pebbles / chips thrown from (x, y, z) with base velocity (vx, vy, vz) ± spread
   *  (m/s), size (m) ± 50%, in colour col (hex) with a little variation; they bounce and settle like the blast rocks. */
  pebbles(x: number, y: number, z: number, n: number, vx: number, vy: number, vz: number, spread: number, size: number, col: number, life = 1.8): void {
    for (let k = 0; k < n; k++) {
      _c.setHex(col).multiplyScalar(0.8 + Math.random() * 0.35);
      this.rocks.add(x + (Math.random() - 0.5) * 0.4, y, z + (Math.random() - 0.5) * 0.4, vx + (Math.random() - 0.5) * 2 * spread, vy + Math.random() * spread, vz + (Math.random() - 0.5) * 2 * spread,
        size * (0.5 + Math.random()), life * (0.7 + Math.random() * 0.6), _c);
    }
  }
  /** smoke pool fill 0..1 (MAP-FIRE throttles its smoke columns with it) */
  get smokeLoad(): number { return this.smoke.load; }
  /** Riftline wall base (owner 2026-11-02 rising wall wave): one low dust puff carried along (vx, vz); tone 0 = pale dust,
   *  1 = darker smoke. Skipped when the smoke pool is busy. */
  dust(x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, s0: number, s1: number, tone = 0): void {
    if (this.smoke.load > 0.8) return;
    _c.copy(DUST).lerp(SMOKE_MID, tone).multiplyScalar(0.9 + Math.random() * 0.15);
    this.smoke.add(x, y, z, vx, vy, vz, life, s0, s1, _c);
  }
  /** soft coloured puff (map debris palette: snow shaken off branches); skipped when the smoke pool is busy */
  puff(x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, s0: number, s1: number, col: number): void {
    if (this.smoke.load > 0.7) return;
    _c.setHex(col).multiplyScalar(0.92 + Math.random() * 0.1);
    this.smoke.add(x, y, z, vx, vy, vz, life, s0, s1, _c);
  }
  /** Surge activation (owner 2026-11-01: 'no explosions… just the aura coming out… wind… swirling of wind and dirt and
   *  particles'): a tangential dust vortex that spirals outward and up around the hero, plus pebbles lifted off the ground. */
  windSwirl(x: number, z: number, tier: number): void {
    const gy = 0.2, nd = Math.round((14 + tier * 6) * (CROWD.busy > 0.5 ? 0.5 : 1));
    for (let k = 0; k < nd; k++) {
      const a = (k / nd) * 6.283 + Math.random() * 0.4, R = 0.8 + Math.random() * 1.4, sp = 3 + tier + Math.random() * 2;
      // velocity = mostly tangential (swirl) + some outward
      const vx = -Math.sin(a) * sp + Math.cos(a) * sp * 0.6, vz = Math.cos(a) * sp + Math.sin(a) * sp * 0.6;
      _c.copy(DUST).multiplyScalar(0.9 + Math.random() * 0.2);
      this.smoke.add(x + Math.cos(a) * R, gy + Math.random() * 0.5, z + Math.sin(a) * R, vx, 0.6 + Math.random() * 1.4, vz, 0.8 + Math.random() * 0.5, 0.4, 1.4 + Math.random() * 0.6 + tier * 0.2, _c);
    }
    const nr = 10 + tier * 6;
    for (let k = 0; k < nr; k++) {
      const a = Math.random() * 6.283, R = 1 + Math.random() * 2.2, sp = 2 + Math.random() * 3;
      this.rocks.add(x + Math.cos(a) * R, gy + 0.1, z + Math.sin(a) * R, -Math.sin(a) * sp, 3 + Math.random() * 4, Math.cos(a) * sp, 0.06 + Math.random() * 0.1, 1.2 + Math.random() * 0.8, DEBRIS.grit[k % DEBRIS.grit.length]);
    }
  }
  /** Pulse Nova: dome + racing shock ring + echo ring, and a dust skirt kicked up along the ring's path */
  novaWave(x: number, y: number, z: number, r: number, gold: boolean): void {
    this.nova.add(x, y, z, r, gold);
    for (let k = 0; k < 14; k++) { const a = Math.random() * 6.283, sp = 12 + Math.random() * 10; _c.setHex(gold ? 0x3a2a10 : 0x0e1630); this.ink.add(x + Math.cos(a) * 1.2, 0.5, z + Math.sin(a) * 1.2, Math.cos(a) * sp, 2 + Math.random() * 3, Math.sin(a) * sp, 0.4 + Math.random() * 0.2, 0.7 + Math.random() * 0.8, 0.14, _c); }
    const nd = Math.round(Math.min(18, 8 + r) * (CROWD.busy > 0.5 ? 0.5 : 1));
    for (let k = 0; k < nd; k++) {
      const a = (k / nd) * 6.283 + Math.random() * 0.3, R = r * (0.75 + Math.random() * 0.2);
      _c.copy(DUST).multiplyScalar(0.95 + Math.random() * 0.15);
      this.smoke.add(x + Math.cos(a) * R, 0.3, z + Math.sin(a) * R, Math.cos(a) * 3.5, 0.5, Math.sin(a) * 3.5, 0.7 + Math.random() * 0.4, 0.5, 1.6 + Math.random(), _c);
    }
  }
  clear(): void { this.smoke.clear(); this.rocks.clear(); this.fire.clear(); this.nova.clear(); this.ink.clear(); this.slabs.clear(); }
  update(dt: number, camQ: THREE.Quaternion): void { this.smoke.update(dt, camQ); this.rocks.update(dt); this.fire.update(dt); this.nova.update(dt); this.ink.update(dt); this.slabs.update(dt); }
}
const _t = new THREE.Color();
